import React, { useMemo } from 'react'
import { Image, ImageStyle, TextStyle, View } from 'react-native'
import { useSelector } from 'react-redux'
import { Icon, IconName } from '@src/components/base'
import NS from '@src/assets/svgs/ns'
import { StyleService, useStyleSheet } from '@src/style/service'
import { CoachAssignmentState } from '@src/types'
import { NotificationInboxAvatarKind } from '@src/components/notifications/hooks/useNotificationInboxPresentation'
import { userSelector } from '@src/selectors/app'
import { coachAssignmentForCoachingKindSelector } from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'

interface NotificationSenderAvatarProps {
  avatarKind: NotificationInboxAvatarKind
  iconName?: IconName
}

export const NotificationSenderAvatar = ({
  avatarKind,
  iconName,
}: NotificationSenderAvatarProps) => {
  const styles = useStyleSheet(themedStyles)
  const user = useSelector(userSelector)

  const primaryCoachingKind =
    user && 'primaryCoachingKind' in user ? user.primaryCoachingKind : undefined

  const coachAssignmentSelector = useMemo(
    () =>
      primaryCoachingKind?.key
        ? coachAssignmentForCoachingKindSelector(primaryCoachingKind.key)
        : () => undefined,
    [primaryCoachingKind],
  )
  const coachAssignment = useSelector(coachAssignmentSelector)

  const activePrimaryCoachAssignment =
    coachAssignment?.state !== CoachAssignmentState.Inactive ? coachAssignment : undefined

  const activePrimaryCoachPhotoUri = activePrimaryCoachAssignment?.coach?.coachProfile?.photo

  if (iconName) {
    return (
      <View style={[styles.avatarCircle]} testID="NotificationSenderAvatar/IconOverride">
        <Icon name={iconName} size={18} style={styles.overrideAvatarIcon} />
      </View>
    )
  }

  if (avatarKind === 'brand') {
    const { color } = styles.brandAvatarText as TextStyle

    return (
      <View style={[styles.avatarCircle]} testID="NotificationSenderAvatar/Brand">
        <NS width={34} height={34} color={color as string} />
      </View>
    )
  }

  if (avatarKind === 'nora') {
    return (
      <View style={[styles.avatarCircle]} testID="NotificationSenderAvatar/Nora">
        <Icon name="star-four" size={18} style={styles.noraAvatarIcon} />
      </View>
    )
  }

  if (activePrimaryCoachPhotoUri) {
    return (
      <View style={styles.avatarCircle}>
        <Image
          source={{ uri: activePrimaryCoachPhotoUri }}
          style={styles.coachAvatarPhoto as ImageStyle}
          testID="NotificationSenderAvatar/CoachPhoto"
        />
      </View>
    )
  }

  return (
    <View style={[styles.avatarCircle]} testID="NotificationSenderAvatar/Coach">
      <Icon name="user" size={18} style={styles.coachAvatarIcon} />
    </View>
  )
}

const themedStyles = StyleService.create({
  avatarCircle: {
    alignItems: 'center',
    backgroundColor: 'theme.disabled.background',
    justifyContent: 'center',
    borderRadius: 20,
    height: 40,
    width: 40,
  },
  coachAvatarIcon: {
    color: 'theme.text.secondary',
  },
  coachAvatarPhoto: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
    backgroundColor: 'theme.disabled.background',
  },
  noraAvatarIcon: {
    color: 'theme.text.secondary',
  },
  overrideAvatarIcon: {
    color: 'theme.text.secondary',
  },
  brandAvatarText: {
    color: 'theme.text.secondary',
  },
})
